import { Capability, FeatureFlag, IntegrationID, Team } from '__types__';
import { ArrowLeft, Blocks, Home, Settings, Shield, Users } from 'lucide-react';
import { useRestrictedAccessCallback } from 'lib/restrictedAccess/useRestrictedAccess';
import { useSelector } from 'react-redux';
import { selectIntegrationById } from 'store/integrations';
import type { RootStore } from 'store/reducer';
import {
  getPublicCompanyName,
  selectAppId
} from 'store/v1/auth/auth.selectors';
import { withinSettings } from 'v4/core/appRoutes';
import { appRouting } from 'v5/core/routing/appRouting';
import { useTranslation } from 'react-i18next';
import { WithServices } from 'v5/platform/ts/services';
import { NavLink } from 'v5/platform/react-router';
import { Button } from 'v5/design-system/Button';
import {
  MenuCollapsible,
  MenuItem,
  MenuSeparator
} from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarGroup,
  SidebarHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { WithDevOriginService } from 'v5/services/front/DevOriginService/DevOriginService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import {
  WithContentThemeService,
  WithThemeService
} from 'v5/services/front/ThemeService/ThemeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { cn } from 'v5/platform/dom/cn';

export interface SettingsSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithSupportService,
      WithThemeService,
      WithContentThemeService,
      WithOnlineStatusService,
      WithDevOriginService,
      WithFeatureService,
      WithAppScopeService
    ]
  > {
  className?: string;
}

export function SettingsSidebar({ services, className }: SettingsSidebarProps) {
  const { t } = useTranslation('core', { keyPrefix: 'resourceGroupsAdmin' });
  const can = useRestrictedAccessCallback();
  const appId = useSelector(selectAppId);
  const companyName = useSelector(getPublicCompanyName);
  const to = withinSettings(appId);

  // Teams temporarily locked to one active team (same as v4)
  const lockedTeam: Partial<Team> = {
    dbSchema: appId,
    name: companyName,
    id: appId
  };

  const isVisitorEnabled = useSelector(
    (state: RootStore) =>
      selectIntegrationById(state, IntegrationID.VISITOR_MANAGEMENT)?.enabled
  );

  // Integration access check (same as v4 IntegrationRestrictedAccess)
  const canAccessIntegrations =
    can(FeatureFlag.CUSTOM_SPLASHLIGHT, Capability.CONFIGURE_INTEGRATIONS) ||
    can(FeatureFlag.CUSTOM_SHOTFLOW, null, null) ||
    can(null, Capability.CONFIGURE_INTEGRATIONS);

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex items-center gap-2 min-h-[var(--header-height)] px-2">
        <NavLink to={`/app/${appId}/dashboard`}>
          <Button variant="ghost" size="sm" layout="icon">
            <ArrowLeft className="size-4" />
          </Button>
        </NavLink>
        <span className="flex-1 text-fg-default">Settings</span>
        <DockableSidebarToggle>
          <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
        </DockableSidebarToggle>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        {/* Your account section */}
        <SidebarGroup label="Your account">
          <NavLink to={to.settings.account.details()}>
            <MenuItem icon={<Users className="size-4" />}>
              Personal account
            </MenuItem>
          </NavLink>
          <NavLink to={to.settings.account.security()}>
            <MenuItem icon={<Shield className="size-4" />}>Security</MenuItem>
          </NavLink>
        </SidebarGroup>

        {/* Organization section */}
        <SidebarGroup label="Organization">
          {can(null, Capability.CONFIGURE_WORKSPACE) && (
            <NavLink to={to.settings.organization.workspace()}>
              <MenuItem icon={<Home className="size-4" />}>Workspace</MenuItem>
            </NavLink>
          )}
          {can(null, Capability.CONFIGURE_TEAM) && (
            <NavLink to={to.settings.organization.users()}>
              <MenuItem icon={<Users className="size-4" />}>Users</MenuItem>
            </NavLink>
          )}
          {can(null, Capability.CONFIGURE_AUTHENTICATION_METHODS) && (
            <NavLink to={to.settings.organization.auth()}>
              <MenuItem icon={<Shield className="size-4" />}>
                Authentication methods
              </MenuItem>
            </NavLink>
          )}
          {canAccessIntegrations && (
            <NavLink to={to.settings.organization.integrations()}>
              <MenuItem icon={<Blocks className="size-4" />}>
                Integrations
              </MenuItem>
            </NavLink>
          )}
        </SidebarGroup>

        {/* Teams section */}
        <SidebarGroup label="Teams">
          <MenuCollapsible
            icon={
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-[7pt] font-bold">
                {lockedTeam.name?.substring(0, 2).toUpperCase()}
              </div>
            }
            label={lockedTeam.name || 'Team Settings'}
            defaultOpen={true}
          >
            {can(null, Capability.CONFIGURE_WORKSPACE) && (
              <NavLink to={to.settings.team.publicProfile()}>
                <MenuItem level={1}>Public profile</MenuItem>
              </NavLink>
            )}
            {can(null, Capability.CONFIGURE_FIELDS) && (
              <NavLink to={to.settings.team.fields()}>
                <MenuItem level={1}>Fields</MenuItem>
              </NavLink>
            )}
            {can(null, Capability.CONFIGURE_TAGS) && (
              <NavLink to={to.settings.team.tags()}>
                <MenuItem level={1}>Tags</MenuItem>
              </NavLink>
            )}
            {can(null, Capability.CONFIGURE_STATUSES) && (
              <NavLink to={to.settings.team.statuses()}>
                <MenuItem level={1}>Statuses</MenuItem>
              </NavLink>
            )}

            <MenuSeparator level={1} />

            {can(
              FeatureFlag.RESOURCES,
              Capability.CONFIGURE_RESOURCE_TYPES
            ) && (
              <NavLink to={to.settings.team.resourceTypes()}>
                <MenuItem level={1}>Resource types</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.RESOURCES,
              Capability.CONFIGURE_RESOURCE_TYPES
            ) && (
              <NavLink
                to={appRouting.org(appId).settings.global.resourceGroups()}
              >
                <MenuItem level={1}>{t('groups')}</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.RESOURCES,
              Capability.CONFIGURE_RESOURCE_TYPES
            ) && (
              <NavLink to={to.settings.team.pronounsOptions()}>
                <MenuItem level={1}>Pronouns options</MenuItem>
              </NavLink>
            )}
            {can(FeatureFlag.RESOURCES, Capability.CONFIGURE_RATE_CARDS) && (
              <NavLink to={to.settings.team.rateCards()}>
                <MenuItem level={1}>Rate cards</MenuItem>
              </NavLink>
            )}

            <MenuSeparator level={1} />

            {can(
              FeatureFlag.PRODUCTIONS,
              Capability.CONFIGURE_PRODUCTION_TYPES
            ) && (
              <NavLink to={to.settings.team.productionTypes()}>
                <MenuItem level={1}>Production types</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.PRODUCTIONS,
              Capability.CONFIGURE_PRODUCTION_TEMPLATES
            ) && (
              <NavLink to={to.settings.team.productionTemplates()}>
                <MenuItem level={1}>Production templates</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.CALLSHEETS,
              Capability.CONFIGURE_CALLSHEET_TEMPLATES
            ) && (
              <NavLink to={to.settings.team.callsheetTemplates()}>
                <MenuItem level={1}>Callsheet templates</MenuItem>
              </NavLink>
            )}
            {can(FeatureFlag.BOOKINGS, Capability.CONFIGURE_EXPENSE_TYPES) && (
              <NavLink to={to.settings.team.expenseTypes()}>
                <MenuItem level={1}>Expense types</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.PRODUCTIONS,
              Capability.CONFIGURE_SLOT_CATEGORIES
            ) && (
              <NavLink to={to.settings.team.slotCategories()}>
                <MenuItem level={1}>Slot categories</MenuItem>
              </NavLink>
            )}
            {can(FeatureFlag.PRODUCTIONS, Capability.CONFIGURE_ALERTS) && (
              <NavLink to={to.settings.team.alerts()}>
                <MenuItem level={1}>Alerts</MenuItem>
              </NavLink>
            )}

            <MenuSeparator level={1} />

            {can(FeatureFlag.BOOKINGS, Capability.CONFIGURE_BOOKING_TYPES) && (
              <NavLink to={to.settings.team.bookingTypes()}>
                <MenuItem level={1}>Booking types</MenuItem>
              </NavLink>
            )}
            {can(
              FeatureFlag.BOOKING_CONFIRMATION,
              Capability.CONFIGURE_BOOKING_CONFIRMATION_TEMPLATES
            ) && (
              <NavLink to={to.settings.team.confirmationTemplates()}>
                <MenuItem level={1}>Confirmation templates</MenuItem>
              </NavLink>
            )}

            {can(
              FeatureFlag.RESOURCES,
              Capability.MANAGE_RESOURCE_DUPLICATES
            ) && (
              <>
                <MenuSeparator level={1} />
                <NavLink to={to.settings.team.duplicates()}>
                  <MenuItem level={1}>Duplicates</MenuItem>
                </NavLink>
              </>
            )}

            <MenuSeparator level={1} />

            {can(
              FeatureFlag.NIKECUSTOM_ARRIVAL_BOARD,
              Capability.CONFIGURE_CUSTOM_NIKE_ARRIVAL_BOARD
            ) && (
              <NavLink to={to.settings.team.arrivalBoard()}>
                <MenuItem level={1}>Arrival board</MenuItem>
              </NavLink>
            )}

            <MenuSeparator level={1} />

            {can(
              FeatureFlag.NIKECUSTOM_QR_CODE,
              Capability.CONFIGURE_CUSTOM_NIKE_QR_CODE
            ) && (
              <NavLink to={to.settings.team.qrCodeMapping()}>
                <MenuItem level={1}>QR Code</MenuItem>
              </NavLink>
            )}
            {isVisitorEnabled && can(null, Capability.MANAGE_VISITORS) && (
              <NavLink to={to.settings.team.visitorManagement()}>
                <MenuItem level={1}>Visitor Management</MenuItem>
              </NavLink>
            )}
          </MenuCollapsible>
        </SidebarGroup>
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}
